import './ReadingList.css';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchBooks, fetchSimplifiedBookById } from '../../services/bookService';
import * as journalListService from '../../services/journalListService';
import { getQuestionsByJournal } from '../../services/questionService';
import { useAuth } from '../../context/AuthContext';
import { useRulesValidation } from '../../hooks/useRulesValidation';
import { canAddBook, generateRuleId, generateRuleLabel, generateMaturitaRules, RULE_TYPES, LITERATURE_TYPES } from '../../utils/ruleValidation';
import { isInYearRange } from '../../utils/centuryUtils';
import RulesSection from './RulesSection';
import BookSuggestions from './BookSuggestions';
import UnsavedChangesModal from '../UnsavedChangesModal/UnsavedChangesModal';

// Helper function to check if a country is Czech (same as in ruleValidation.js)
// Checks if the country string contains "česk" (case insensitive)
const isCzechCountry = (country) => {
    if (!country) return false;
    return country.toLowerCase().includes('česk');
};

// Helper function to check if a book matches a single rule (for client-side filtering)
const bookMatchesRule = (book, rule) => {
    switch (rule.type) {
        case RULE_TYPES.BOOKS_BY_TYPE:
            return book.bookType === rule.bookType;

        case RULE_TYPES.BOOKS_BY_COUNTRY:
            if (rule.literatureType === LITERATURE_TYPES.CZECH) {
                return isCzechCountry(book.countryOfOrigin);
            } else if (rule.literatureType === LITERATURE_TYPES.WORLD) {
                return book.countryOfOrigin && !isCzechCountry(book.countryOfOrigin);
            } else if (rule.country) {
                return book.countryOfOrigin === rule.country;
            }
            return false;

        case RULE_TYPES.BOOKS_BY_YEAR_RANGE:
            return isInYearRange(book.publicationYear, rule.yearFrom, rule.yearTo);

        default:
            return true;
    }
};

function ReadingList() {
    const navigate = useNavigate();
    const { isLoggedIn } = useAuth();
    const skipBlockRef = useRef(false);
    const [selectedBooks, setSelectedBooks] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [showResults, setShowResults] = useState(false);

    // Saved list state
    const [listExists, setListExists] = useState(false);
    const [listLoading, setListLoading] = useState(false);
    const [savedBookIds, setSavedBookIds] = useState([]);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Quiz modal state
    const [showQuizModal, setShowQuizModal] = useState(false);
    const [quizQuestionCount, setQuizQuestionCount] = useState('10');
    const [quizLoading, setQuizLoading] = useState(false);
    const [quizError, setQuizError] = useState('');

    // Rules state
    const [rules, setRules] = useState([]);

    // Suggestions state
    const [suggestions, setSuggestions] = useState([]);
    const [currentSuggestionRule, setCurrentSuggestionRule] = useState(null);
    const [suggestionsPage, setSuggestionsPage] = useState(0);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [suggestionsHasMore, setSuggestionsHasMore] = useState(false);

    // Validation hook
    const { validationStatus } = useRulesValidation(rules, selectedBooks);

    // Unsaved changes detection
    const hasUnsavedChanges = isLoggedIn
        ? [...selectedBooks.map(b => b.id)].sort().join() !== [...savedBookIds].sort().join()
        : selectedBooks.length > 0;

    // Load existing list for logged-in users
    useEffect(() => {
        if (!isLoggedIn) return;

        const loadList = async () => {
            setListLoading(true);
            try {
                const data = await journalListService.getJournalList();
                if (data) {
                    const ids = data.readingJournalIds || [];
                    setSavedBookIds(ids);
                    setListExists(true);
                    const books = await Promise.all(ids.map(id => fetchSimplifiedBookById(id)));
                    setSelectedBooks(books);
                }
            } catch (err) {
                console.error('Error loading journal list:', err);
            } finally {
                setListLoading(false);
            }
        };

        loadList();
    }, [isLoggedIn]);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Search books
    useEffect(() => {
        const performSearch = async () => {
            if (debouncedSearchTerm.trim().length < 2) {
                setSearchResults([]);
                setShowResults(false);
                return;
            }

            try {
                setSearching(true);
                // Search by both title and authorName with the same term
                const filters = {
                    title: debouncedSearchTerm.trim(),
                    authorName: debouncedSearchTerm.trim()
                };

                const results = await searchBooks(filters, 0, 10);
                setSearchResults(results.content || []);
                setShowResults(true);
            } catch (err) {
                console.error('Error searching books:', err);
                setSearchResults([]);
                setShowResults(false);
            } finally {
                setSearching(false);
            }
        };

        performSearch();
    }, [debouncedSearchTerm]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // Core add logic shared by search and suggestions — purely local
    const performAddBook = (book) => {
        if (selectedBooks.find(b => b.id === book.id)) {
            alert('Tato kniha je již v seznamu.');
            return false;
        }

        const addCheck = canAddBook(book, selectedBooks, rules);
        if (!addCheck.canAdd) {
            alert(addCheck.reason);
            return false;
        }

        setSelectedBooks(prev => [...prev, book]);
        return true;
    };

    const handleAddBook = (book) => {
        const added = performAddBook(book);
        if (added) {
            setSearchTerm('');
            setSearchResults([]);
            setShowResults(false);
        }
    };

    const handleRemoveBook = (bookId) => {
        setSelectedBooks(prev => prev.filter(b => b.id !== bookId));
    };

    const handleSaveList = async () => {
        setSaving(true);
        setSaveError('');
        setSaveSuccess(false);
        try {
            if (!listExists) {
                await journalListService.createJournalList();
                setListExists(true);
            }
            const currentIds = selectedBooks.map(b => b.id);
            const toAdd = currentIds.filter(id => !savedBookIds.includes(id));
            const toRemove = savedBookIds.filter(id => !currentIds.includes(id));
            for (const id of toRemove) {
                await journalListService.removeJournalFromList(id);
            }
            for (const id of toAdd) {
                await journalListService.addJournalToList(id);
            }
            setSavedBookIds(currentIds);
            setSaveSuccess(true);
        } catch (err) {
            console.error('Save failed:', err);
            setSaveError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteList = async () => {
        if (!window.confirm('Opravdu chcete smazat celý uložený seznam?')) return;
        setSaveError('');
        try {
            await journalListService.deleteJournalList();
            setSelectedBooks([]);
            setSavedBookIds([]);
            setListExists(false);
        } catch (err) {
            setSaveError(err.message);
        }
    };

    const handleStartQuiz = async () => {
        if (isLoggedIn) {
            handleSaveList();
        }

        const count = parseInt(quizQuestionCount, 10);
        if (!count || count < 1) {
            setQuizError('Zadejte platný počet otázek.');
            return;
        }
        setQuizLoading(true);
        setQuizError('');
        try {
            // Fetch questions for all books in parallel
            const results = await Promise.all(
                selectedBooks.map(book =>
                    getQuestionsByJournal(book.id).catch(() => [])
                )
            );

            // Flatten, deduplicate by ID, and annotate with book/author info
            const seen = new Set();
            const pool = [];
            for (let i = 0; i < results.length; i++) {
                const book = selectedBooks[i];
                for (const q of results[i]) {
                    if (!seen.has(q.id)) {
                        seen.add(q.id);
                        pool.push({
                            ...q,
                            _bookTitle: book.bookTitle,
                            _authorName: book.author?.name,
                            _bookId: book.id,
                        });
                    }
                }
            }

            if (pool.length === 0) {
                setQuizError('Pro vybrané knihy nebyly nalezeny žádné otázky.');
                return;
            }

            // Fisher-Yates shuffle
            for (let i = pool.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [pool[i], pool[j]] = [pool[j], pool[i]];
            }

            const selected = pool.slice(0, count);

            setShowQuizModal(false);
            skipBlockRef.current = true;
            navigate('/kvizy/muj-seznam/start', {
                state: {
                    preloadedRawQuestions: selected,
                    returnPath: '/seznam-cetby',
                }
            });
        } catch (err) {
            setQuizError('Nepodařilo se načíst otázky: ' + err.message);
        } finally {
            setQuizLoading(false);
        }
    };

    const handleBookClick = (bookId) => {
        navigate(`/knihy/${bookId}`);
    };

    // Rules management callbacks
    const handleAddRule = (newRule) => {
        setRules([...rules, newRule]);
    };

    const handleDeleteRule = (ruleId) => {
        setRules(rules.filter(rule => rule.id !== ruleId));
    };

    // Maturita preset handler
    const handleActivateMaturita = () => {
        if (rules.length > 0) {
            const confirmed = window.confirm(
                'Nahradit stávající pravidla maturitními pravidly dle CERMAT?'
            );
            if (!confirmed) return;
        }
        setRules(generateMaturitaRules());
    };

    // Suggestions handlers
    const handleSuggestBooks = async (rule) => {
        try {
            setSuggestionsLoading(true);
            setCurrentSuggestionRule(rule);
            setSuggestionsPage(0);
            setSuggestions([]);

            // Build filters based on rule type
            const filters = {};
            let needsClientSideFiltering = false;
            let clientSideRules = []; // For combined rules client-side filtering

            if (rule.type === RULE_TYPES.COMBINED) {
                // For combined rules, we need to check all nested rules
                // and apply them with AND logic
                needsClientSideFiltering = true;
                clientSideRules = rule.combinedRules;

                // Try to apply as many filters on backend as possible
                rule.combinedRules.forEach(nestedRule => {
                    if (nestedRule.type === RULE_TYPES.BOOKS_BY_TYPE && !filters.bookType) {
                        filters.bookType = nestedRule.bookType;
                    } else if (nestedRule.type === RULE_TYPES.BOOKS_BY_COUNTRY && nestedRule.literatureType === LITERATURE_TYPES.CZECH && !filters.countryOfOrigin) {
                        filters.countryOfOrigin = 'Česko';
                    }
                });
            } else {
                switch (rule.type) {
                    case RULE_TYPES.BOOKS_BY_TYPE:
                        filters.bookType = rule.bookType;
                        break;

                    case RULE_TYPES.BOOKS_BY_COUNTRY:
                        if (rule.literatureType === LITERATURE_TYPES.CZECH) {
                            // Czech literature - filter by backend
                            filters.countryOfOrigin = 'Česko';
                        } else if (rule.literatureType === LITERATURE_TYPES.WORLD) {
                            // World literature - need client-side filtering (everything except Česko)
                            needsClientSideFiltering = true;
                        } else if (rule.country) {
                            // Fallback for old rules
                            filters.countryOfOrigin = rule.country;
                        }
                        break;

                    case RULE_TYPES.BOOKS_BY_YEAR_RANGE:
                        // Backend doesn't support year range filtering, so we'll filter on client side
                        // Fetch larger page size and filter
                        needsClientSideFiltering = true;
                        break;

                    case RULE_TYPES.TOTAL_COUNT:
                        // No filters — return any books
                        break;

                    default:
                        // This should not happen as the suggest button is only shown for supported types
                        alert('Návrhy pro tento typ pravidla nejsou podporovány');
                        setSuggestionsLoading(false);
                        return;
                }
            }

            const SUGGESTIONS_PAGE_SIZE = needsClientSideFiltering ? 50 : 10;
            const results = await searchBooks(filters, 0, SUGGESTIONS_PAGE_SIZE);

            // Filter out books that are already in the selected list
            const selectedBookIds = selectedBooks.map(b => b.id);
            let filteredSuggestions = results.content.filter(
                book => !selectedBookIds.includes(book.id)
            );

            // Apply client-side filtering if needed
            if (needsClientSideFiltering) {
                if (rule.type === RULE_TYPES.COMBINED) {
                    // For combined rules, book must match ALL nested rules (AND logic)
                    filteredSuggestions = filteredSuggestions.filter(book =>
                        clientSideRules.every(nestedRule => bookMatchesRule(book, nestedRule))
                    );
                } else if (rule.type === RULE_TYPES.BOOKS_BY_YEAR_RANGE) {
                    filteredSuggestions = filteredSuggestions.filter(book =>
                        isInYearRange(book.publicationYear, rule.yearFrom, rule.yearTo)
                    );
                } else if (rule.type === RULE_TYPES.BOOKS_BY_COUNTRY && rule.literatureType === LITERATURE_TYPES.WORLD) {
                    // World literature - exclude Czech books
                    filteredSuggestions = filteredSuggestions.filter(book =>
                        book.countryOfOrigin && !isCzechCountry(book.countryOfOrigin)
                    );
                }
            }

            setSuggestions(filteredSuggestions);
            setSuggestionsHasMore(results.page.totalPages > 1);
        } catch (err) {
            console.error('Error fetching suggestions:', err);
            alert('Nepodařilo se načíst návrhy. Zkuste to prosím znovu.');
        } finally {
            setSuggestionsLoading(false);
        }
    };

    const handleLoadMoreSuggestions = async () => {
        if (!currentSuggestionRule || suggestionsLoading) return;

        try {
            setSuggestionsLoading(true);
            const nextPage = suggestionsPage + 1;

            // Build filters same as in handleSuggestBooks
            const filters = {};
            let needsClientSideFiltering = false;
            let clientSideRules = [];

            if (currentSuggestionRule.type === RULE_TYPES.COMBINED) {
                needsClientSideFiltering = true;
                clientSideRules = currentSuggestionRule.combinedRules;

                // Try to apply as many filters on backend as possible
                currentSuggestionRule.combinedRules.forEach(nestedRule => {
                    if (nestedRule.type === RULE_TYPES.BOOKS_BY_TYPE && !filters.bookType) {
                        filters.bookType = nestedRule.bookType;
                    } else if (nestedRule.type === RULE_TYPES.BOOKS_BY_COUNTRY && nestedRule.literatureType === LITERATURE_TYPES.CZECH && !filters.countryOfOrigin) {
                        filters.countryOfOrigin = 'Česko';
                    }
                });
            } else {
                switch (currentSuggestionRule.type) {
                    case RULE_TYPES.BOOKS_BY_TYPE:
                        filters.bookType = currentSuggestionRule.bookType;
                        break;
                    case RULE_TYPES.BOOKS_BY_COUNTRY:
                        if (currentSuggestionRule.literatureType === LITERATURE_TYPES.CZECH) {
                            filters.countryOfOrigin = 'Česko';
                        } else if (currentSuggestionRule.literatureType === LITERATURE_TYPES.WORLD) {
                            needsClientSideFiltering = true;
                        } else if (currentSuggestionRule.country) {
                            filters.countryOfOrigin = currentSuggestionRule.country;
                        }
                        break;
                    case RULE_TYPES.BOOKS_BY_YEAR_RANGE:
                        needsClientSideFiltering = true;
                        break;
                    case RULE_TYPES.TOTAL_COUNT:
                        break;
                    default:
                        return;
                }
            }

            const SUGGESTIONS_PAGE_SIZE = needsClientSideFiltering ? 50 : 10;
            const results = await searchBooks(filters, nextPage, SUGGESTIONS_PAGE_SIZE);

            // Filter out books that are already in the selected list or already in suggestions
            const selectedBookIds = selectedBooks.map(b => b.id);
            const existingSuggestionIds = suggestions.map(b => b.id);
            let filteredSuggestions = results.content.filter(
                book => !selectedBookIds.includes(book.id) && !existingSuggestionIds.includes(book.id)
            );

            // Apply client-side filtering if needed
            if (needsClientSideFiltering) {
                if (currentSuggestionRule.type === RULE_TYPES.COMBINED) {
                    // For combined rules, book must match ALL nested rules (AND logic)
                    filteredSuggestions = filteredSuggestions.filter(book =>
                        clientSideRules.every(nestedRule => bookMatchesRule(book, nestedRule))
                    );
                } else if (currentSuggestionRule.type === RULE_TYPES.BOOKS_BY_YEAR_RANGE) {
                    filteredSuggestions = filteredSuggestions.filter(book =>
                        isInYearRange(book.publicationYear, currentSuggestionRule.yearFrom, currentSuggestionRule.yearTo)
                    );
                } else if (currentSuggestionRule.type === RULE_TYPES.BOOKS_BY_COUNTRY && currentSuggestionRule.literatureType === LITERATURE_TYPES.WORLD) {
                    filteredSuggestions = filteredSuggestions.filter(book =>
                        book.countryOfOrigin && !isCzechCountry(book.countryOfOrigin)
                    );
                }
            }

            setSuggestions([...suggestions, ...filteredSuggestions]);
            setSuggestionsPage(nextPage);
            setSuggestionsHasMore(nextPage + 1 < results.page.totalPages);
        } catch (err) {
            console.error('Error loading more suggestions:', err);
            alert('Nepodařilo se načíst další návrhy.');
        } finally {
            setSuggestionsLoading(false);
        }
    };

    const handleAddFromSuggestion = (book) => {
        const added = performAddBook(book);
        if (added) {
            setSuggestions(prev => prev.filter(b => b.id !== book.id));
        }
    };

    const handleCloseSuggestions = () => {
        setCurrentSuggestionRule(null);
        setSuggestions([]);
        setSuggestionsPage(0);
    };

    const handleCombineRules = (ruleIds) => {
        if (ruleIds.length < 2) {
            alert('Vyberte alespoň 2 pravidla pro kombinaci');
            return;
        }

        // Get the selected rules
        const selectedRules = rules.filter(rule => ruleIds.includes(rule.id));

        // Flatten: extract nested rules from combined rules
        const flattenedRules = [];
        selectedRules.forEach(rule => {
            if (rule.type === RULE_TYPES.COMBINED) {
                // Add all nested rules from combined rule
                flattenedRules.push(...rule.combinedRules);
            } else {
                flattenedRules.push(rule);
            }
        });

        // Check maximum limit
        if (flattenedRules.length > 4) {
            alert('Kombinované pravidlo může mít maximálně 4 podmínky');
            return;
        }

        // Check for duplicate types
        const types = flattenedRules.map(rule => rule.type);
        const uniqueTypes = new Set(types);
        if (types.length !== uniqueTypes.size) {
            alert('Nelze kombinovat pravidla stejného typu');
            return;
        }

        // Generate combined label from all flattened rules
        const combinedLabel = flattenedRules.map(rule => generateRuleLabel(rule)).join(', ');

        // Create new combined rule
        const combinedRule = {
            id: generateRuleId(),
            type: RULE_TYPES.COMBINED,
            combinedLabel,
            combinedRules: flattenedRules
        };

        // Remove selected rules and add combined rule
        setRules([
            ...rules.filter(rule => !ruleIds.includes(rule.id)),
            combinedRule
        ]);
    };

    return (
        <div className="reading-list-page">
            <div className="reading-list-container">
                <h1 className="page-title">Seznam četby</h1>
                <p className="page-description">
                    Přidejte knihy do seznamu pro četbu.
                </p>

                {listLoading && (
                    <div className="list-status-info">Načítám uložený seznam...</div>
                )}

                {/* Quiz CTA */}
                <div className={`quiz-cta-banner${selectedBooks.length > 0 ? ' quiz-cta-ready' : ''}`}>
                    <div className="quiz-cta-text">
                        <span className="quiz-cta-title">Kvíz ze seznamu četby</span>
                        <span className="quiz-cta-desc">
                            {selectedBooks.length > 0
                                ? `${selectedBooks.length} ${selectedBooks.length === 1 ? 'kniha připravena' : selectedBooks.length < 5 ? 'knihy připraveny' : 'knih připraveno'}`
                                : 'Přidejte knihy do seznamu a otestujte své znalosti'}
                        </span>
                    </div>
                    <button
                        className="btn-quiz-cta"
                        onClick={() => { setShowQuizModal(true); setQuizError(''); }}
                        disabled={selectedBooks.length === 0}
                    >
                        Vytvořit kvíz
                    </button>
                </div>

                {/* Search Section */}
                <div className="search-section">
                    <h2 className="section-title">Přidat knihu</h2>
                    <div className="search-bar-container">
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Vyhledat podle názvu nebo autora..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            />
                        {searching && <div className="search-loading">Hledám...</div>}
                    </div>

                    {/* Search Results */}
                    {showResults && searchResults.filter(b => !selectedBooks.find(s => s.id === b.id)).length > 0 && (
                        <div className="search-results">
                            {searchResults.filter(b => !selectedBooks.find(s => s.id === b.id)).map(book => (
                                <div key={book.id} className="search-result-item">
                                    <div
                                        className="result-info"
                                        onClick={() => handleBookClick(book.id)}
                                        style={{ cursor: 'pointer', flex: 1 }}
                                    >
                                        <h3 className="result-title">{book.bookTitle}</h3>
                                        <p className="result-author">{book.author?.name}</p>
                                    </div>
                                    <button
                                        className="btn-add"
                                        onClick={() => handleAddBook(book)}
                                    >
                                        + Přidat
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {showResults && !searching && searchResults.filter(b => !selectedBooks.find(s => s.id === b.id)).length === 0 && (
                        <div className="no-results">
                            Nenalezeny žádné knihy.
                        </div>
                    )}
                </div>

                {/* Maturita Banner */}
                <div className="maturita-banner">
                    <div className="maturita-banner-content">
                        <div className="maturita-banner-text">
                            <span className="maturita-banner-title">Maturitní seznam dle CERMAT</span>
                            <span className="maturita-banner-desc">
                                Automaticky nastaví pravidla dle CERMAT: do konce 18. stol. min. 2, 19. stol. min. 3,
                                světová 20./21. stol. min. 4, česká 20./21. stol. min. 5,
                                min. 2 prózy / 2 poezie / 2 dramata, max. 2 díla od autora.
                            </span>
                        </div>
                        <button
                            className="btn-maturita-activate"
                            onClick={handleActivateMaturita}
                        >
                            Aktivovat
                        </button>
                    </div>
                </div>

                {/* Rules Section */}
                <RulesSection
                    rules={rules}
                    validationStatus={validationStatus}
                    onAddRule={handleAddRule}
                    onDeleteRule={handleDeleteRule}
                    onCombineRules={handleCombineRules}
                    onSuggest={handleSuggestBooks}
                />

                {/* Selected Books Section */}
                <div className="selected-books-section">
                    <div className="selected-books-header">
                        <h2 className="section-title">
                            Vybrané knihy ({selectedBooks.length})
                        </h2>
                        <div className="list-actions">
                            {isLoggedIn && (
                                <>
                                    <button
                                        className="btn-save-list"
                                        onClick={handleSaveList}
                                        disabled={saving}
                                    >
                                        {saving ? 'Ukládám...' : 'Uložit seznam'}
                                    </button>
                                    {listExists && (
                                        <button className="btn-delete-list" onClick={handleDeleteList}>
                                            Smazat seznam
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {saveSuccess && !saveError && (
                        <div className="list-save-success">Seznam byl úspěšně uložen.</div>
                    )}
                    {saveError && (
                        <div className="list-sync-error">{saveError}</div>
                    )}

                    {selectedBooks.length === 0 ? (
                        <div className="empty-list">
                            <p>Zatím nemáte v seznamu žádné knihy.</p>
                            <p>Začněte vyhledáváním výše.</p>
                        </div>
                    ) : (
                        <div className="selected-books-list">
                            {selectedBooks.map((book, index) => (
                                <div key={book.id} className="selected-book-item">
                                    <span className="book-number">{index + 1}.</span>
                                    <div
                                        className="book-info"
                                        onClick={() => handleBookClick(book.id)}
                                        style={{ cursor: 'pointer', flex: 1 }}
                                    >
                                        <h3 className="book-title">{book.bookTitle}</h3>
                                        <p className="book-author">{book.author?.name}</p>
                                    </div>
                                    <button
                                        className="btn-remove"
                                        onClick={() => handleRemoveBook(book.id)}
                                        title="Odebrat knihu"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Quiz Creation Modal */}
                {showQuizModal && (
                    <div className="quiz-modal-overlay" onClick={() => setShowQuizModal(false)}>
                        <div className="quiz-modal" onClick={e => e.stopPropagation()}>
                            <h2 className="quiz-modal-title">Vytvořit kvíz ze seznamu</h2>
                            <p className="quiz-modal-desc">
                                Otázky budou náhodně vybrány z {selectedBooks.length} knih ve vašem seznamu.
                            </p>
                            <label className="quiz-modal-label" htmlFor="quiz-count">
                                Počet otázek
                            </label>
                            <div className="quiz-modal-stepper">
                                <button
                                    className="quiz-stepper-btn"
                                    onClick={() => setQuizQuestionCount(v => String(Math.max(1, parseInt(v, 10) - 1)))}
                                    disabled={quizLoading || parseInt(quizQuestionCount, 10) <= 1}
                                    type="button"
                                >−</button>
                                <input
                                    id="quiz-count"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    className="quiz-modal-input"
                                    value={quizQuestionCount}
                                    onChange={e => {
                                        const val = e.target.value.replace(/\D/g, '');
                                        setQuizQuestionCount(val);
                                    }}
                                    disabled={quizLoading}
                                />
                                <button
                                    className="quiz-stepper-btn"
                                    onClick={() => setQuizQuestionCount(v => String(parseInt(v, 10) + 1))}
                                    disabled={quizLoading}
                                    type="button"
                                >+</button>
                            </div>
                            {quizError && (
                                <div className="quiz-modal-error">{quizError}</div>
                            )}
                            <div className="quiz-modal-actions">
                                <button
                                    className="btn-quiz-cancel"
                                    onClick={() => setShowQuizModal(false)}
                                    disabled={quizLoading}
                                >
                                    Zrušit
                                </button>
                                <button
                                    className="btn-quiz-start"
                                    onClick={handleStartQuiz}
                                    disabled={quizLoading}
                                >
                                    {quizLoading ? 'Načítám otázky...' : 'Spustit kvíz'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Book Suggestions Modal */}
                {currentSuggestionRule && (
                    <BookSuggestions
                        rule={currentSuggestionRule}
                        suggestions={suggestions}
                        onAddBook={handleAddFromSuggestion}
                        onLoadMore={handleLoadMoreSuggestions}
                        onClose={handleCloseSuggestions}
                        loading={suggestionsLoading}
                        hasMore={suggestionsHasMore}
                        onBookClick={handleBookClick}
                    />
                )}

                <UnsavedChangesModal
                    isActive={hasUnsavedChanges}
                    skipRef={skipBlockRef}
                    message={isLoggedIn
                        ? 'Máte neuložené změny v seznamu četby. Pokud odejdete, přijdete o ně.'
                        : 'Váš seznam četby nebude uložen. Přihlaste se, abyste mohli seznam uložit, nebo pokračujte bez uložení.'}
                />
            </div>
        </div>
    );
}

export default ReadingList;
